/**
 * scan-export.js — window.__scanExport: the codes a scanner read, as a FILE.
 *
 * WHY IT EXISTS (bug report 24.10.2026, /ru/qr-scanner, Yandex 25 on Windows):
 *   «Невозможно скачать просто содержащийся в QR-коде текст в первоначальном
 *    виде… В файле CSV к слову Мосэнергосбыт добавляются дополнительные
 *    кавычки, а в файле JSON — знаки \».
 * Both files were right for their formats and wrong for the person:
 *   • JSON escapes " as \" — a format for programs, not for reading;
 *   • the CSV (UTF-8 + BOM, commas) opened by a double-click in a Russian
 *     Excel is not split into columns at all: the list separator there is «;»,
 *     so the whole row lands in cell A with its quotes doubled. Measured
 *     through Excel COM on a ru-RU Windows (Local:=True — what a double-click
 *     does), 29.10.2026: our file — one column, «""Мосэнергосбыт""»;
 *     «sep=,» first — mojibake (Excel drops the BOM); «;» — right in ru, one
 *     column in en-US; UTF-16 + TAB — columns in both, but a code is still read
 *     as a number there: EAN 04600051000057 → 4,6E+12.
 * No CSV opens right in every Excel. So a person gets
 *   • TEXT (.txt) — the codes exactly as they are: one per line (a blank line
 *     between codes when one of them spans lines), no BOM, nothing added;
 *   • a TABLE (.xlsx, public/js/xlsx-writer.js) — the same in every Excel,
 *     LibreOffice and Google Sheets; a code is text (leading zeros stay), and
 *     a control character (GS in a marking code) survives as OOXML _xHHHH_;
 * and the machine formats stay what they are, DATA for programs: CSV
 * (RFC 4180, comma, UTF-8 with BOM) and JSON — their shape is each tool's
 * contract with whatever imports them, so the tool builds the rows.
 *
 * API (window.__scanExport):
 *   text(texts)                      -> string   the codes, joined as above
 *   csv(rows, columns)               -> string   columns: [{key, head}]
 *   xlsx(rows, columns, opts)        -> Promise<Uint8Array>
 *        columns: [{key, head, kind: 'time'|'code'|'label', width}];
 *        a 'time' value is a timestamp (ms), written as the reader's own date
 *        and time (Excel built-in format 22); opts: {sheet, assets: {fflate, xlsxWriter}}
 *   save(blobParts, name, type)      -> Promise<boolean>  through __downloadFile
 * ONE code, «its text as is» on a result card:
 *   visible(text, cls)               -> HTML   escaped; a control character (GS between
 *        the fields of a marking code) drawn as its Unicode picture in <span class=cls>
 *   fileName(c, r, symbolLabel)      -> 'QR Code - АО Мосэнергосбыт.txt' — the name
 *        carries what is in the file (output-filename.js); c = classify() verdict
 *   saveText(text, name)             -> Promise<boolean>  the text alone, no BOM
 * Consumers: /qr-scanner, /barcode-scanner.
 */
(function (root) {
    'use strict';
    if (root.__scanExport) return;

    var EOL = '\r\n';

    function hasBreak(s) { return /[\r\n]/.test(s); }

    function text(texts) {
        var list = (texts || []).map(function (t) { return String(t == null ? '' : t); });
        return list.join(list.some(hasBreak) ? EOL + EOL : EOL);
    }

    function csvCell(v) { return '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"'; }
    function csv(rows, columns) {
        var out = [columns.map(function (c) { return c.head; }).join(',')];
        (rows || []).forEach(function (r) {
            out.push(columns.map(function (c) { return csvCell(r[c.key]); }).join(','));
        });
        return '\uFEFF' + out.join(EOL);
    }

    /* Excel's day count with the reader's own clock: a scan at 10:24 in Moscow
       reads 10:24, as the history list on the page does. */
    var EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
    function excelTime(ts) {
        var d = new Date(ts);
        if (isNaN(d)) return null;
        return (ts - d.getTimezoneOffset() * 60000 - EXCEL_EPOCH_MS) / 864e5;
    }

    function load(assets, key, expect) {
        if (root[expect]) return Promise.resolve();
        var url = assets && assets[key];
        if (!url || typeof root.__vendorScript !== 'function') return Promise.reject(new Error('scan_export_no_' + key));
        return root.__vendorScript(url, { expect: expect, label: key });
    }

    function xlsx(rows, columns, opts) {
        opts = opts || {};
        return Promise.all([load(opts.assets, 'fflate', 'fflate'), load(opts.assets, 'xlsxWriter', 'XlsxWriter')]).then(function () {
            var head = { font: { b: true }, fill: '#E8EEF5', border: { b: 'thin' }, align: { v: 'center' } };
            var cells = [];
            columns.forEach(function (c, i) { cells.push({ r: 1, c: i + 1, v: c.head, s: head }); });
            (rows || []).forEach(function (r, ri) {
                columns.forEach(function (c, ci) {
                    var v = r[c.key], cell = { r: ri + 2, c: ci + 1 };
                    if (c.kind === 'time') {
                        var t = excelTime(v);
                        cell.v = t == null ? '' : t;
                        cell.s = { numFmt: 22, align: { h: 'left', v: 'top' } };
                    } else {
                        // a code is TEXT: «04600051000057» keeps its zero, «1E5» stays a code
                        cell.v = v == null ? '' : String(v);
                        cell.s = c.kind === 'code' ? { align: { wrap: true, v: 'top' }, numFmt: 49 } : { align: { v: 'top' } };
                    }
                    cells.push(cell);
                });
            });
            var last = String.fromCharCode(64 + columns.length);
            var n = (rows || []).length + 1;
            var codeCols = [];
            columns.forEach(function (c, i) { if (c.kind === 'code') codeCols.push(String.fromCharCode(65 + i)); });
            var sheet = {
                name: opts.sheet || 'Scans',
                cols: columns.map(function (c) { return c.width || (c.kind === 'code' ? 70 : c.kind === 'time' ? 18 : 20); }),
                cells: cells,
                freeze: { row: 1 },
                autoFilter: 'A1:' + last + n,
                ignoredErrors: codeCols.map(function (L) { return { sqref: L + '2:' + L + Math.max(2, n), numberStoredAsText: true }; }),
            };
            return root.XlsxWriter.build({ title: opts.title || '', sheets: [sheet] }, root.fflate.zipSync);
        });
    }

    function save(parts, name, type) {
        var blob = new Blob(parts, { type: type });
        if (typeof root.__downloadFile === 'function') return Promise.resolve(root.__downloadFile(blob, name));
        return Promise.resolve(false);
    }

    /* «As it is» on the screen: the copy and the file carry the real characters; the
       eye gets a picture for the ones that have no glyph (U+2400 + code, DEL — U+2421). */
    function escHtml(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
    function visible(t, cls) {
        return escHtml(t == null ? '' : t).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, function (ch) {
            var n = ch.charCodeAt(0), pic = n === 127 ? '\u2421' : String.fromCharCode(0x2400 + n);
            return '<span class="' + escHtml(cls || 'scan-ctl') + '" title="U+' + ('000' + n.toString(16).toUpperCase()).slice(-4) + '">' + pic + '</span>';
        });
    }

    /* No BOM and nothing else: a payment string that starts with U+FEFF before
       «ST00012» is not a payment string to the program it is pasted into. */
    function saveText(t, name) { return save([String(t == null ? '' : t)], name, 'text/plain;charset=utf-8'); }

    /* The payee of a bill, the site of a link, the network, the product number —
       after the symbol's name. */
    function fileName(c, r, symbol) {
        c = c || {}; r = r || {};
        var f = c.fields || {}, P = root.__BarcodeParsers, stem = '';
        switch (c.type) {
            case 'payment': stem = f.Name || f.PersonalAcc || ''; break;
            case 'url': stem = (c.risk && c.risk.hostname) || ''; break;
            case 'wifi': stem = c.ssid || ''; break;
            case 'vcard': case 'mecard': stem = f.name || ''; break;
            case 'vevent': stem = c.summary || ''; break;
            case 'otpauth': stem = c.issuer || ''; break;
            case 'gs1': stem = (P && P.productGtin && P.productGtin(c)) || ''; break;
            case 'product': stem = c.code || ''; break;
            case 'isbn': stem = c.isbn || ''; break;
            case 'vin': stem = c.vin || ''; break;
            default: stem = String(r.text || '').split(/[\r\n]/)[0].slice(0, 40);
        }
        var base = (symbol || 'code') + (stem ? ' - ' + stem : '');
        var N = root.__outputName;
        if (N && N.sanitize && N.join) return N.join(N.sanitize(base) || 'code', 'txt');
        return base.replace(/[\\/:*?"<>|\u0000-\u001F]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) + '.txt';
    }

    root.__scanExport = { text: text, csv: csv, xlsx: xlsx, save: save, visible: visible, saveText: saveText, fileName: fileName, _excelTime: excelTime };
})(typeof window !== 'undefined' ? window : globalThis);
